import React, {useEffect, useState} from 'react';
import {
    Alert,
    Box,
    Button,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    Link as MuiLink,
    Paper,
    TextField,
    Typography
} from '@mui/material';
import {Link, useNavigate} from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import {saveUser} from '../helpers/crypto';
import {Constants} from "../components/Constants";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID as string;
const API_URL = Constants.SERVER_URL;

declare global {
    interface Window { google: any; }
}

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => {
            window.google?.accounts.id.initialize({
                client_id: GOOGLE_CLIENT_ID,
                callback: handleGoogleResponse,
            });
            window.google?.accounts.id.renderButton(
                document.getElementById('google-signin-btn'),
                { theme: 'outline', size: 'large', width: '100%', text: 'signin_with', locale: 'uk' }
            );
        };
        document.body.appendChild(script);
        return () => { document.body.removeChild(script); };
    }, []);

    const handleGoogleResponse = async (response: any) => {
        setError('');
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}${Constants.GOOGLE_AUTH_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ credential: response.credential }),
            });
            const data = await res.json();
            if (data.success) {
                saveUser(data.user);
                navigate('/');
            } else {
                setError(data.error || 'Помилка авторизації через Google');
            }
        } catch {
            setError('Помилка з\'єднання з сервером');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!email || !password) {
            setError('Введіть email та пароль');
            return;
        }

        setLoading(true);
        try {
            const res = await fetch(`${API_URL}${Constants.AUTH_PREFIX}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json();
            if (data.success) {
                saveUser(data.user);
                navigate('/');
            } else {
                setError(data.error || 'Помилка авторизації');
            }
        } catch(err) {
            setError('Помилка з\'єднання з сервером '+err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f7f9fc', display: 'flex', alignItems: 'center', py: 4 }}>
            <Container maxWidth="sm">
                <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3, border: '1px solid #e0e6ed' }}>
                    {/* Logo */}
                    <Box sx={{ textAlign: 'center', mb: 4 }}>
                        <Box component={Link} to="/" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, textDecoration: 'none' }}>
                            <LocalHospitalIcon sx={{ color: '#0077CC', fontSize: 32 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e' }}>{Constants.APP_LABEL}</Typography>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, mt: 3, mb: 0.5 }}>Вхід в акаунт</Typography>
                        <Typography sx={{ color: '#555' }}>Увійдіть щоб оформити замовлення</Typography>
                    </Box>

                    {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

                    {/* Google */}
                    <Box id="google-signin-btn" sx={{ mb: 3, display: 'flex', justifyContent: 'center' }} />

                    <Divider sx={{ mb: 3 }}>
                        <Typography sx={{ color: '#999', fontSize: 13 }}>або через email</Typography>
                    </Divider>

                    {/* Form */}
                    <Box component="form" onSubmit={handleSubmit}>
                        <TextField
                            fullWidth label="Email" type="email" value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            sx={{ mb: 2.5 }}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start"><EmailIcon sx={{ color: '#999' }} />
                                    </InputAdornment>
                                }
                            }}
                        />
                        <TextField
                            fullWidth label="Пароль" type={showPassword ? 'text' : 'password'}
                            value={password} onChange={(e) => setPassword(e.target.value)}
                            sx={{ mb: 3 }}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start"><LockIcon sx={{ color: '#999' }} />
                                    </InputAdornment>,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }
                            }}
                        />
                        <Button
                            type="submit" variant="contained" fullWidth size="large" disabled={loading}
                            sx={{ textTransform: 'none', borderRadius: 2, py: 1.5, mb: 2 }}
                        >
                            {loading ? 'Завантаження...' : 'Увійти'}
                        </Button>
                    </Box>

                    <Typography sx={{ textAlign: 'center', color: '#555', fontSize: 14 }}>
                        Немає акаунту?{' '}
                        <MuiLink component={Link} to="/register" underline="hover" sx={{ fontWeight: 600 }}>
                            Зареєструватися
                        </MuiLink>
                    </Typography>
                </Paper>
            </Container>
        </Box>
    );
};

export default LoginPage;
